import { useState, useRef, useEffect, useCallback, type ReactNode } from 'react';
import { Search, X, ArrowLeft, ChevronDown } from 'lucide-react';
import { useMapStore } from '../../stores/mapStore';
import { SearchSuggestions } from './SearchSuggestions';
import { autocompleteMinLength } from '../../utils/buildingNumberSearch';
import { ReleaseBanner } from '../ui/ReleaseBanner';
import { OutOfRegionBanner } from '../ui/OutOfRegionBanner';

interface SearchBarProps {
  onGetDirections?: () => void;
  onNavigateToStop?: (name: string, coords: { lat: number; lng: number }) => void;
  onAddMissingPlace?: () => void;
  /** Second row below search — map menu / profile controls. */
  controlsRow?: ReactNode;
}

export function SearchBar({ onGetDirections, onNavigateToStop, onAddMissingPlace, controlsRow }: SearchBarProps) {
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const chromeRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<NodeJS.Timeout | null>(null);

  const [showFilters, setShowFilters] = useState(false);

  const {
    searchQuery,
    searchType,
    setSearchQuery,
    setSearchType,
    performAutocomplete,
    clearSearch,
    showSearchResults,
    setShowSearchResults,
    selectedPlace,
    clearSelectedPlace,
    selectPlace,
    setSheetState,
    userLocation,
  } = useMapStore();

  const prevUserLocationRef = useRef<typeof userLocation>(undefined);

  const SEARCH_FILTERS = [
    { value: 'all', label: 'All' },
    { value: 'poi', label: 'Places' },
    { value: 'building', label: 'Buildings' },
    { value: 'street', label: 'Streets' },
    { value: 'postcode', label: 'Postcode' },
    { value: 'gps', label: 'GPS Code' },
    { value: 'city', label: 'Cities' },
    { value: 'district', label: 'Districts' },
    { value: 'region', label: 'Regions' },
  ] as const;

  const handleFilterChange = useCallback((value: string) => {
    setSearchType(value);
    // Re-trigger search/autocomplete with the new filter
    if (searchQuery.trim().length >= autocompleteMinLength(searchQuery.trim())) {
      // Small delay so store updates first
      setTimeout(() => performAutocomplete(searchQuery.trim()), 50);
    }
  }, [setSearchType, searchQuery, performAutocomplete]);

  // Debounced autocomplete - triggers as user types
  const handleInputChange = useCallback((value: string) => {
    setSearchQuery(value);

    // Clear previous debounce timer
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    // Debounce autocomplete API call (400ms delay; 2 chars for house numbers, else 3)
    debounceRef.current = setTimeout(() => {
      const trimmed = value.trim();
      if (trimmed.length >= autocompleteMinLength(trimmed)) {
        performAutocomplete(trimmed);
      }
    }, 400);
  }, [setSearchQuery, performAutocomplete]);

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      performAutocomplete(searchQuery.trim());
      inputRef.current?.blur();
    }
  };

  const handleFocus = () => {
    setIsFocused(true);
    // If a place is already selected, clear it so user can search for something new
    if (selectedPlace) {
      clearSelectedPlace();
    }
    // Show search results panel, don't trigger new autocomplete
    // This preserves existing search results when user clicks back into input
    setShowSearchResults(true);
    // Collapse bottom sheet when interacting with search
    setSheetState('collapsed');
    // NOTE: Don't trigger autocomplete on focus - only on typing
    // This prevents clearing search results when user clicks input
  };

  const handleBack = () => {
    if (selectedPlace) {
      clearSelectedPlace();
    } else {
      clearSearch();
      setIsFocused(false);
    }
  };

  const handleClear = () => {
    setSearchQuery('');
    inputRef.current?.focus();
  };

  // Close suggestions and filter dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest('.search-container')) {
        setIsFocused(false);
      }
      if (!target.closest('.filter-dropdown')) {
        setShowFilters(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  // Re-fire autocomplete with distance when location first becomes available
  useEffect(() => {
    const wasNull = prevUserLocationRef.current == null;
    prevUserLocationRef.current = userLocation;
    if (wasNull && userLocation && searchQuery.trim().length >= 3) {
      performAutocomplete(searchQuery.trim());
    }
  }, [userLocation, searchQuery, performAutocomplete]);

  // Update search query when a place is selected
  useEffect(() => {
    if (selectedPlace && selectedPlace.name) {
      setSearchQuery(selectedPlace.name);
    }
  }, [selectedPlace, setSearchQuery]);

  const isActive = isFocused || showSearchResults;

  useEffect(() => {
    const el = chromeRef.current;
    if (!el) return;

    const updateChromeHeight = () => {
      document.documentElement.style.setProperty(
        '--map-top-chrome-height',
        `${el.offsetHeight}px`,
      );
    };

    updateChromeHeight();
    const observer = new ResizeObserver(updateChromeHeight);
    observer.observe(el);

    return () => {
      observer.disconnect();
      document.documentElement.style.removeProperty('--map-top-chrome-height');
    };
  }, [isActive]);

  return (
    <div className={`search-container fixed top-0 left-0 right-0 safe-area-top ${isActive ? 'bottom-0 bg-white dark:bg-gray-900 flex flex-col z-[60]' : 'z-40'
      }`}>
      <div ref={chromeRef} className={isActive ? 'flex-shrink-0' : undefined}>
      <div className={isActive ? 'p-3 flex-shrink-0' : 'p-3'}>
        <form onSubmit={handleSubmit}>
          <div data-tour-target="search" className={`flex items-center px-4 py-3 gap-3 ${isActive
            ? 'bg-gray-100 dark:bg-gray-900/95 rounded-xl'
            : 'bg-white dark:bg-gray-900/95 backdrop-blur-xl rounded-full shadow-lg dark:shadow-black/50 dark:border dark:border-gray-700/50'
            }`}>
            {/* Back/Menu button */}
            {(isFocused || selectedPlace) ? (
              <button
                type="button"
                onClick={handleBack}
                className="flex-shrink-0 p-1 -ml-1 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors"
              >
                <ArrowLeft size={22} />
              </button>
            ) : (
              <Search size={22} className="flex-shrink-0 text-gray-400 dark:text-gray-500" />
            )}

            {/* Search input */}
            <input
              ref={inputRef}
              type="text"
              data-testid="map-search-input"
              placeholder="Search Ghana..."
              value={searchQuery}
              onChange={(e) => handleInputChange(e.target.value)}
              onFocus={handleFocus}
              autoComplete="off"
              className="flex-1 min-w-0 outline-none text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 text-base bg-transparent text-ellipsis overflow-hidden [&:-webkit-autofill]:bg-transparent [&:-webkit-autofill]:[-webkit-text-fill-color:inherit] [&:-webkit-autofill]:[transition:background-color_9999s_ease-in-out_0s]"
            />

            {/* Clear button */}
            {searchQuery && (
              <button
                type="button"
                onClick={handleClear}
                className="flex-shrink-0 p-1 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              >
                <X size={20} />
              </button>
            )}

            {/* Active filter pill inside search bar */}
            <div className="relative filter-dropdown flex-shrink-0">
              <button
                type="button"
                onClick={() => setShowFilters(!showFilters)}
                className="flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap transition-colors bg-primary-600 text-white"
              >
                {SEARCH_FILTERS.find(f => f.value === searchType)?.label || 'All'}
                <ChevronDown size={12} className={`transition-transform ${showFilters ? 'rotate-180' : ''}`} />
              </button>

              {/* Filter dropdown */}
              {showFilters && (
                <div className="absolute right-0 top-full mt-2 w-40 bg-white dark:bg-gray-900 rounded-xl shadow-lg dark:shadow-black/50 border border-gray-200 dark:border-gray-700/50 py-1 z-50">
                  {SEARCH_FILTERS.map((filter) => (
                    <button
                      key={filter.value}
                      type="button"
                      onClick={() => { handleFilterChange(filter.value); setShowFilters(false); }}
                      className={`w-full text-left px-4 py-2 text-sm transition-colors ${searchType === filter.value
                        ? 'bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 font-medium'
                        : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'
                        }`}
                    >
                      {filter.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </form>
      </div>

      {controlsRow && !isActive ? (
        <div className="px-3 pb-2 flex items-start min-w-0">
          {controlsRow}
        </div>
      ) : null}

      {!isActive && (
        <div className="flex flex-col">
          <ReleaseBanner />
          <OutOfRegionBanner />
        </div>
      )}
      </div>

      {/* Full-page search results - fills remaining space */}
      {isActive && (
        <div className="flex-1 overflow-y-auto">
          <SearchSuggestions
            onClose={() => {
              setIsFocused(false);
              setShowSearchResults(false);
            }}
            onGetDirections={(placeId: string) => {
              selectPlace(placeId);
              setIsFocused(false);
              setShowSearchResults(false);
              if (onGetDirections) {
                onGetDirections();
              }
            }}
            onNavigateToStop={onNavigateToStop}
            onAddMissingPlace={onAddMissingPlace ? () => {
              setIsFocused(false);
              setShowSearchResults(false);
              onAddMissingPlace();
            } : undefined}
          />
        </div>
      )}
    </div>
  );
}
